import { Box, Grid } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { moveProducts } from '../../api/store/moveProducts';
import EditModal from '../../components/EditModal';
import FormComponentsTable, { FormComponentsTableProps } from '../../components/forms/FormComponentsTable';
import FormSelect from '../../components/forms/FormSelect';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';

type FormType = {
  fromStoreId: string;
  toStoreId: string;
  components: { id: string; qnt: number }[];
  showMaxQnt: boolean;
};

type Props = {
  onClose: () => void;
};

const MoveItems = ({ onClose }: Props) => {
  const { cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { ingredients, stores, storesMap, worker } = useCafe();

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      fromStoreId: '',
      toStoreId: '',
      components: [],
    },
  });

  const { fromStoreId, toStoreId, components } = form.watch();
  const fromStore = useMemo(() => stores.find((s) => s.id === fromStoreId), [fromStoreId, stores]);
  const toStore = useMemo(() => stores.find((s) => s.id === toStoreId), [toStoreId, stores]);

  const options = useMemo(
    () =>
      ingredients
        .filter((i) => fromStore?.residues[i.id]! > 0)
        .map((i) => ({
          ...i,
          maxQnt: fromStore?.residues[i.id],
        })),
    [ingredients, fromStore?.residues]
  );

  const saveChanges = async () => {
    if (fromStore && toStore && worker) {
      await moveProducts({ cafeSlug, fromStore, toStore, components, worker });

      onClose();
    }
  };

  return (
    <EditModal<FormType>
      title={t.move[lang]}
      open
      onClose={onClose}
      submitButton={t.move[lang]}
      onSubmit={saveChanges}
      form={form}
      size="sm"
    >
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormSelect
              name="fromStoreId"
              options={stores.filter((s) => s.id !== toStoreId).map((s) => s.id)}
              label={t.from_store[lang]}
              fullWidth
              rules={{ required: true }}
              renderOption={(o) => storesMap[o]?.name}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormSelect
              name="toStoreId"
              options={stores.filter((s) => s.id !== fromStoreId).map((s) => s.id)}
              label={t.to_store[lang]}
              fullWidth
              rules={{ required: true }}
              renderOption={(o) => storesMap[o]?.name}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, minHeight: 200 }}>
          <FormComponentsTable
            form={form as unknown as FormComponentsTableProps['form']}
            options={options}
            title={t.component[lang]}
          />
        </Box>
      </Box>
    </EditModal>
  );
};

export default MoveItems;
